import { getGlobalVariables } from '@libs/core';
import { TCommonData, TWidget, TViewCommonOption } from '@libs/types';
import clsx from 'clsx';
import React, { Activity, CSSProperties, useMemo, useState } from 'react';
import { useThemeHook } from '../hooks/themeHook';
import { TCoreImageOptions, ALIGNMENT_ENUM } from '../types/options';
import { buildImageCssVars } from './buildImageCssVars';
import { mergeWithTheme } from '../utils/styles';
import { Image } from './Image';
import styles from './image.module.css';

const ALIGNMENT_CLASS: Record<string, string> = {
    [ALIGNMENT_ENUM.TOP_LEFT]: styles.alignTopLeft,
    [ALIGNMENT_ENUM.TOP_CENTER]: styles.alignTopCenter,
    [ALIGNMENT_ENUM.TOP_RIGHT]: styles.alignTopRight,
    [ALIGNMENT_ENUM.CENTER_LEFT]: styles.alignCenterLeft,
    [ALIGNMENT_ENUM.CENTER]: styles.alignCenter,
    [ALIGNMENT_ENUM.CENTER_RIGHT]: styles.alignCenterRight,
    [ALIGNMENT_ENUM.BOTTOM_LEFT]: styles.alignBottomLeft,
    [ALIGNMENT_ENUM.BOTTOM_CENTER]: styles.alignBottomCenter,
    [ALIGNMENT_ENUM.BOTTOM_RIGHT]: styles.alignBottomRight,
};

const getObjectFit = (objectFit?: TCoreImageOptions['objectFit']): string => {
    if (objectFit === 'fill') return 'fill';
    if (objectFit === 'cover') return 'cover';
    return 'contain';
};

/**
 * Global variable holding a comma-separated list of icon identifiers that must NOT be recolored via the SVG mask.
 * Each entry is matched as a substring of the image `src` (e.g. a filename or path fragment), so multicolor icons
 * keep their original colors and render as a plain <img> instead of a single-color masked SVG.
 */
const ICONS_WITHOUT_COLOR_MASK_VAR = 'iconsWithoutColorMask';

export type TCoreImageStyleProps = Pick<
    TCoreImageOptions,
    | 'width'
    | 'defaultIconWidth'
    | 'height'
    | 'defaultIconHeight'
    | 'borderRadius'
    | 'objectFit'
    | 'iconColor'
    | 'iconColorDisabled'
    | 'alignment'
    | 'margin'
    | 'spinnerDelay'
    | 'overflow'
>;

export type TCoreImageViewProps = {
    /**
     * Used solely for theme resolution via useThemeHook. No behavior or interaction logic is derived from it here.
     * Optional: when omitted, a minimal widget is synthesized internally from `src`. Provide it only when the
     * widget originates server-side (so theme styles via `widget.options.style` are honored).
     */
    widget?: TWidget<TCommonData, TCoreImageOptions>;
    src?: string;
    defaultSrc?: string;
    defaultIcon?: string;
    /** Style overrides merged on top of the resolved theme palette via mergeWithTheme. */
    styleProps?: TCoreImageStyleProps;
} & Pick<TViewCommonOption, 'isHide' | 'isDisabled' | 'styleName' | 'parentStyleNames'>;

/**
 * Composable building block for use inside complex widgets.
 * Owns only the theme hook (useThemeHook) to keep the hook count low
 * when embedded inside larger widget trees.
 *
 * Accepts pre-resolved behavior props (isHide) from the parent, making it
 * suitable for scenarios where behavior state is already managed one level up.
 *
 * `widget` is optional — when omitted, a minimal widget is synthesized from `src`,
 * which is sufficient for standalone icon/image usages that don't need server-driven theme styles.
 */
const CoreImageView = ({ widget, isDisabled, isHide, src = '', defaultSrc, defaultIcon, styleProps, styleName, parentStyleNames }: TCoreImageViewProps) => {
    const resolvedWidget = widget ?? ({ uid: '', data: { value: src }, options: {} } as TWidget<TCommonData, TCoreImageOptions>);
    const enrichedWidget = {
        ...resolvedWidget,
        options: { ...resolvedWidget.options, style: styleName, parentStyleNames },
    };
    const { palette: themePalette } = useThemeHook(enrichedWidget);
    const palette: TCoreImageOptions = mergeWithTheme(themePalette, styleProps);

    const objectFit = getObjectFit(palette.objectFit);

    const globalVariables = getGlobalVariables(ICONS_WITHOUT_COLOR_MASK_VAR);
    const rawIconsWithoutColorMask = globalVariables[ICONS_WITHOUT_COLOR_MASK_VAR];
    const iconsWithoutColorMask = useMemo(
        () =>
            (rawIconsWithoutColorMask ?? '')
                .split(',')
                .map((entry) => entry.trim())
                .filter(Boolean),
        [rawIconsWithoutColorMask],
    );

    const [imgErrorSrc, setImgErrorSrc] = useState<string | null>(null);
    const isImgError = imgErrorSrc === src && !!src;
    const cssVarsPalette = isImgError
        ? { ...palette, width: palette.defaultIconWidth ?? palette.width, height: palette.defaultIconHeight ?? palette.height }
        : palette;

    if (!src && !defaultIcon && !defaultSrc) return null;

    return (
        <Activity mode={isHide ? 'hidden' : 'visible'}>
            <div
                data-test={resolvedWidget.uid}
                className={clsx(styles.container, palette.alignment && ALIGNMENT_CLASS[palette.alignment])}
                style={buildImageCssVars(cssVarsPalette) as CSSProperties}
            >
                <Image
                    src={src}
                    iconColor={palette.iconColor}
                    iconColorDisabled={palette.iconColorDisabled ?? palette.iconColor}
                    isDisabled={isDisabled}
                    width={palette.width}
                    defaultIconWidth={palette.defaultIconWidth}
                    height={palette.height}
                    defaultIconHeight={palette.defaultIconHeight}
                    imgStyles={{ objectFit }}
                    spinnerDelay={palette.spinnerDelay}
                    defaultSrc={defaultSrc}
                    defaultIcon={defaultIcon}
                    iconsWithoutColorMask={iconsWithoutColorMask}
                    onImgError={() => setImgErrorSrc(src)}
                />
            </div>
        </Activity>
    );
};

export { CoreImageView };
